import type { DealFixedRateLoanData, DealFloatingRateLoanData, DealRepoData, OTCDealData } from '@src/types/OTC';
import { formatCounterpartyTypeForDisplay } from '@src/utils/counterpartyType';
import { isUnsecuredLoanSecurityType, resolveSecuredLoanCollateralAsset } from '@src/utils/OTC/loanSecurity';
import { resolveLoanThresholdsFromPayload } from '@src/utils/OTC/loanThresholdFields';
import {
  CollateralHealthStatusEnum,
  DealStatusEnum,
  isFixedRateLoanSecurity,
  isFloatingRateLoanSecurity,
  isOTCRepoSecurity,
  SubProductTypeEnum,
  type CustomerMargin,
  type ICounterparty,
  type ISubAccountCollateralStatus,
  type Security,
} from '@talos/kyoko';
import { getDealJsonObject, type DealJsonObject } from '../OTC/dealJsonObject';

export const CounterpartyMonitoringProductType = {
  Margin: 'Margin',
  FixedRateLoan: 'Fixed Rate Loan',
  FloatingRateLoan: 'Floating Rate Loan',
  Repo: 'Repo',
} as const;

export type CounterpartyMonitoringProductType =
  (typeof CounterpartyMonitoringProductType)[keyof typeof CounterpartyMonitoringProductType];

type CounterpartyMonitoringBaseRow = {
  rowID: string;
  counterpartyType: string;
  Counterparty?: string;
  MarketAccount?: string;
  Symbol?: string;
  Amount?: string;
  Collateral?: string;
  MarginCurrency?: string;
  MarkPrice?: string;
  UnrealizedPnL?: string;
  StressFactor?: string;
  NegativeStressPnL?: string;
  PositiveStressPnL?: string;
  amountCurrency?: string;
};

export type CounterpartyMonitoringLoanCollateralStatus = ISubAccountCollateralStatus;

export type CounterpartyMonitoringRepoCollateralStatus = Omit<ISubAccountCollateralStatus, 'CollateralizationMode'>;

type CounterpartyMonitoringCollateralizedDealRow<TCollateralStatus> = CounterpartyMonitoringBaseRow & {
  collateralStatus?: CollateralHealthStatusEnum;
  collateralStatusRatio?: string;
  collateralStatusDetails?: TCollateralStatus;
  original: OTCDealData;
};

export type CounterpartyMonitoringMarginRow = CounterpartyMonitoringBaseRow & {
  productType: typeof CounterpartyMonitoringProductType.Margin;
  original: CustomerMargin;
};

export type CounterpartyMonitoringLoanRow =
  CounterpartyMonitoringCollateralizedDealRow<CounterpartyMonitoringLoanCollateralStatus> & {
    productType:
      | typeof CounterpartyMonitoringProductType.FixedRateLoan
      | typeof CounterpartyMonitoringProductType.FloatingRateLoan;
    thresholdsLiquidation?: string;
    thresholdsMarginCall?: string;
    thresholdsMinimum?: string;
    thresholdsCollateralRelease?: string;
  };

export type CounterpartyMonitoringRepoRow =
  CounterpartyMonitoringCollateralizedDealRow<CounterpartyMonitoringRepoCollateralStatus> & {
    productType: typeof CounterpartyMonitoringProductType.Repo;
    closeOutMarginPct?: string;
    marginCallPct?: string;
    maintenanceMarginPct?: string;
    marginReleasePct?: string;
  };

export type CounterpartyMonitoringRow =
  | CounterpartyMonitoringMarginRow
  | CounterpartyMonitoringLoanRow
  | CounterpartyMonitoringRepoRow;

type UnionKeys<Union> = Union extends unknown ? keyof Union : never;

export type CounterpartyMonitoringRowField = UnionKeys<CounterpartyMonitoringRow>;

export function isCounterpartyMonitoringLoan(
  row: CounterpartyMonitoringRow | null | undefined
): row is CounterpartyMonitoringLoanRow {
  return (
    row?.productType === CounterpartyMonitoringProductType.FixedRateLoan ||
    row?.productType === CounterpartyMonitoringProductType.FloatingRateLoan
  );
}

export function isCounterpartyMonitoringCollateralizedDeal(
  row: CounterpartyMonitoringRow | null | undefined
): row is CounterpartyMonitoringLoanRow | CounterpartyMonitoringRepoRow {
  return isCounterpartyMonitoringLoan(row) || row?.productType === CounterpartyMonitoringProductType.Repo;
}

/** A collateralized deal row known to carry collateral status details, so its tooltip always has content. */
export type CounterpartyMonitoringCollateralizedDealWithStatusDetails =
  | (CounterpartyMonitoringLoanRow & { collateralStatusDetails: CounterpartyMonitoringLoanCollateralStatus })
  | (CounterpartyMonitoringRepoRow & { collateralStatusDetails: CounterpartyMonitoringRepoCollateralStatus });

export function hasCollateralStatusDetails(
  row: CounterpartyMonitoringLoanRow | CounterpartyMonitoringRepoRow
): row is CounterpartyMonitoringCollateralizedDealWithStatusDetails {
  return row.collateralStatusDetails != null;
}

export function isCounterpartyMonitoringDeal(deal: OTCDealData) {
  return (
    deal.Status === DealStatusEnum.Live &&
    (deal.SubProductType === SubProductTypeEnum.OTCFixedRateLoan ||
      deal.SubProductType === SubProductTypeEnum.OTCFloatingRateLoan ||
      deal.SubProductType === SubProductTypeEnum.OTCRepo)
  );
}

export type CounterpartyMonitoringJsonObject =
  | CustomerMargin
  | (DealJsonObject<'loan'> & { subAccountCollateralStatus?: CounterpartyMonitoringLoanCollateralStatus })
  | (DealJsonObject<'repo'> & { subAccountCollateralStatus?: CounterpartyMonitoringRepoCollateralStatus });

const UNKNOWN_COUNTERPARTY_TYPE = 'Unknown';

function getCounterpartyType(
  counterpartyName: string | undefined,
  counterpartiesByName: Map<string, Pick<ICounterparty, 'Type'>>
) {
  const type = counterpartyName && counterpartiesByName.get(counterpartyName)?.Type;
  return type ? formatCounterpartyTypeForDisplay(type) : UNKNOWN_COUNTERPARTY_TYPE;
}

function getLoanCollateralAsset(security: Security | undefined) {
  if (!isFixedRateLoanSecurity(security) && !isFloatingRateLoanSecurity(security)) {
    return undefined;
  }
  return resolveSecuredLoanCollateralAsset(
    security.ProductDetails.LoanSecurityType,
    security.ProductDetails.CollateralAsset
  );
}

function isUnsecuredLoanSecurity(security: Security | undefined) {
  return (
    (isFixedRateLoanSecurity(security) || isFloatingRateLoanSecurity(security)) &&
    isUnsecuredLoanSecurityType(security.ProductDetails.LoanSecurityType)
  );
}

function getLoanBorrowedAsset(security: Security | undefined) {
  return isFixedRateLoanSecurity(security) || isFloatingRateLoanSecurity(security)
    ? security.ProductDetails.BorrowedAsset
    : undefined;
}

function normalizeLoanDealRow(
  deal: DealFixedRateLoanData | DealFloatingRateLoanData,
  security: Security | undefined,
  counterpartiesByName: Map<string, Pick<ICounterparty, 'Type'>>,
  collateralStatus: ISubAccountCollateralStatus | undefined
): CounterpartyMonitoringLoanRow {
  const priceResult = collateralStatus?.PriceResults?.[0];
  const isUnsecured = isUnsecuredLoanSecurity(security);
  const loanThresholds = resolveLoanThresholdsFromPayload(deal.DealInfo);

  return {
    rowID: deal.ID,
    Counterparty: deal.Counterparty,
    MarketAccount: deal.MarketAccount,
    Symbol: deal.Symbol,
    counterpartyType: getCounterpartyType(deal.Counterparty, counterpartiesByName),
    productType:
      deal.SubProductType === SubProductTypeEnum.OTCFixedRateLoan
        ? CounterpartyMonitoringProductType.FixedRateLoan
        : CounterpartyMonitoringProductType.FloatingRateLoan,
    Amount: deal.DealInfo.LoanAmount,
    amountCurrency: getLoanBorrowedAsset(security),
    Collateral: isUnsecured ? undefined : deal.DealInfo.PledgedCollateral,
    MarginCurrency: getLoanCollateralAsset(security),
    collateralStatus: isUnsecured ? CollateralHealthStatusEnum.Unsecured : priceResult?.HealthStatus,
    collateralStatusRatio: isUnsecured ? undefined : priceResult?.Ratio,
    collateralStatusDetails: isUnsecured ? undefined : collateralStatus,
    thresholdsLiquidation: loanThresholds.liquidation,
    thresholdsMarginCall: loanThresholds.marginCall,
    thresholdsMinimum: loanThresholds.minimum,
    thresholdsCollateralRelease: loanThresholds.collateralRelease,
    original: deal,
  };
}

function normalizeRepoDealRow(
  deal: DealRepoData,
  security: Security | undefined,
  counterpartiesByName: Map<string, Pick<ICounterparty, 'Type'>>,
  collateralStatus: ISubAccountCollateralStatus | undefined
): CounterpartyMonitoringRepoRow {
  const repoSecurity = isOTCRepoSecurity(security) ? security : undefined;
  const priceResult = collateralStatus?.PriceResults?.[0];

  return {
    rowID: deal.ID,
    Counterparty: deal.Counterparty,
    MarketAccount: deal.MarketAccount,
    Symbol: deal.Symbol,
    counterpartyType: getCounterpartyType(deal.Counterparty, counterpartiesByName),
    productType: CounterpartyMonitoringProductType.Repo,
    Amount: deal.DealInfo.PurchasePrice,
    amountCurrency: repoSecurity?.ProductDetails.BorrowedAsset,
    Collateral: deal.DealInfo.RepoAmount,
    MarginCurrency: repoSecurity?.ProductDetails.RepoAsset,
    collateralStatus: priceResult?.HealthStatus,
    collateralStatusRatio: priceResult?.Ratio,
    collateralStatusDetails: collateralStatus,
    closeOutMarginPct: deal.DealInfo.CloseOutMarginPct,
    marginCallPct: deal.DealInfo.MarginCallPct,
    maintenanceMarginPct: deal.DealInfo.MaintenanceMarginPct,
    marginReleasePct: deal.DealInfo.MarginReleasePct,
    original: deal,
  };
}

export function normalizeCustomerMarginRow(
  row: CustomerMargin,
  counterpartiesByName: Map<string, Pick<ICounterparty, 'Type'>>
): CounterpartyMonitoringMarginRow {
  return {
    ...row,
    rowID: row.rowID,
    counterpartyType: getCounterpartyType(row.Counterparty, counterpartiesByName),
    productType: CounterpartyMonitoringProductType.Margin,
    original: row,
  };
}

export function normalizeDealRow(
  deal: OTCDealData,
  securitiesBySymbol: Map<string, Security>,
  counterpartiesByName: Map<string, Pick<ICounterparty, 'Type'>>,
  subAccountCollateralStatusByDealId: Map<string, ISubAccountCollateralStatus> = new Map()
): CounterpartyMonitoringRow | undefined {
  if (!isCounterpartyMonitoringDeal(deal)) {
    return undefined;
  }

  const security = securitiesBySymbol.get(deal.Symbol);

  switch (deal.SubProductType) {
    case SubProductTypeEnum.OTCFixedRateLoan:
    case SubProductTypeEnum.OTCFloatingRateLoan:
      return normalizeLoanDealRow(
        deal,
        security,
        counterpartiesByName,
        subAccountCollateralStatusByDealId.get(deal.ID)
      );
    case SubProductTypeEnum.OTCRepo:
      return normalizeRepoDealRow(
        deal,
        security,
        counterpartiesByName,
        subAccountCollateralStatusByDealId.get(deal.ID)
      );
    default:
      return undefined;
  }
}

export function getCounterpartyMonitoringJsonObject(
  row: CounterpartyMonitoringRow,
  securitiesBySymbol: Map<string, Security>
): CounterpartyMonitoringJsonObject {
  if (row.productType === CounterpartyMonitoringProductType.Margin) {
    return row.original;
  }

  if (row.productType === CounterpartyMonitoringProductType.Repo) {
    return {
      ...getDealJsonObject({
        deal: row.original,
        propertyName: 'repo',
        securitiesBySymbol,
      }),
      subAccountCollateralStatus: row.collateralStatusDetails,
    };
  }

  return {
    ...getDealJsonObject({
      deal: row.original,
      propertyName: 'loan',
      securitiesBySymbol,
    }),
    subAccountCollateralStatus: row.collateralStatusDetails,
  };
}
